import type { Renderer } from '../render/Renderer';
import type { Vec2 } from '../types';

export interface PointerEvent2 {
  id: number;
  pos: Vec2;
  /** Monotonic timestamp (ms) when event fired. */
  time: number;
}

type Handler = (e: PointerEvent2) => void;

export class TouchInput {
  private renderer: Renderer;
  private downHandlers = new Set<Handler>();
  private upHandlers = new Set<Handler>();
  private moveHandlers = new Set<Handler>();
  private activePointers = new Map<number, Vec2>();

  constructor(renderer: Renderer) {
    this.renderer = renderer;
    this.attach();
  }

  private attach(): void {
    const el = this.renderer.canvas;
    const opts: AddEventListenerOptions = { passive: false };

    const handle = (
      handlers: Set<Handler>,
      ev: PointerEvent,
      track: 'add' | 'remove' | null,
    ): void => {
      ev.preventDefault();
      const pos = this.renderer.pageToLogical(ev.clientX, ev.clientY);
      const id = ev.pointerId;
      if (track === 'add') this.activePointers.set(id, pos);
      else if (track === 'remove') this.activePointers.delete(id);
      else if (this.activePointers.has(id)) this.activePointers.set(id, pos);
      const e: PointerEvent2 = { id, pos, time: performance.now() };
      for (const h of handlers) h(e);
    };

    el.addEventListener(
      'pointerdown',
      (ev) => {
        try {
          el.setPointerCapture(ev.pointerId);
        } catch {
          /* some browsers may reject */
        }
        handle(this.downHandlers, ev, 'add');
      },
      opts,
    );
    el.addEventListener('pointerup', (ev) => handle(this.upHandlers, ev, 'remove'), opts);
    el.addEventListener('pointercancel', (ev) => handle(this.upHandlers, ev, 'remove'), opts);
    el.addEventListener('pointermove', (ev) => handle(this.moveHandlers, ev, null), opts);

    // Block context menu / gesture defaults on canvas.
    el.addEventListener('contextmenu', (ev) => ev.preventDefault());
    el.addEventListener('touchstart', (ev) => ev.preventDefault(), opts);
    el.addEventListener('touchmove', (ev) => ev.preventDefault(), opts);
    el.addEventListener('touchend', (ev) => ev.preventDefault(), opts);
  }

  onPointerDown(h: Handler): () => void {
    this.downHandlers.add(h);
    return () => this.downHandlers.delete(h);
  }
  onPointerUp(h: Handler): () => void {
    this.upHandlers.add(h);
    return () => this.upHandlers.delete(h);
  }
  onPointerMove(h: Handler): () => void {
    this.moveHandlers.add(h);
    return () => this.moveHandlers.delete(h);
  }

  isAnyPointerDown(): boolean {
    return this.activePointers.size > 0;
  }
}
